import { schema } from '../schema';
import warning from './logger';

type TimezoneIanaToGraphqlEnumMap = Record<string, string>;

const ianaToGraphqlEnum = (
    schema as unknown as {
        timezoneGraphqlEnumMap: TimezoneIanaToGraphqlEnumMap;
    }
).timezoneGraphqlEnumMap;

const graphqlEnumToIana = Object.fromEntries(
    Object.entries(ianaToGraphqlEnum).map(([ianaTimezone, graphqlEnum]) => [
        graphqlEnum,
        ianaTimezone,
    ])
) as Record<string, string>;

const IANA_TIMEZONE_ALIASES: Record<string, string> = {
    'Europe/Kyiv': 'Europe/Kiev',
};

export function ianaTimezoneToGraphQLEnum(value: string): string {
    const timezone = value.trim();
    const graphqlEnum = ianaToGraphqlEnum[timezone];
    if (!graphqlEnum) {
        throw new Error(`Invalid timezone "${timezone}".`);
    }

    return graphqlEnum;
}

export function safeIanaTimezoneToGraphQLEnum(value: string): string {
    const timezone = value.trim();

    try {
        return ianaTimezoneToGraphQLEnum(timezone);
    } catch (error) {
        const aliasedTimezone = IANA_TIMEZONE_ALIASES[timezone];
        if (aliasedTimezone) {
            try {
                const graphqlEnum = ianaTimezoneToGraphQLEnum(aliasedTimezone);
                warning(
                    `Mapped IANA timezone alias "${timezone}" to "${aliasedTimezone}" for GraphQL enum conversion.`
                );
                return graphqlEnum;
            } catch {
                // Fall through to shared warning path below.
            }
        }

        warning(
            `Falling back to UTC GraphQL timezone enum for unknown IANA timezone "${timezone}".`,
            error as Error
        );
        // Intentional UX tradeoff:
        // keep the form usable with a valid enum default instead of throwing during form render.
        // We emit a warning so backend timezone maps can be updated quickly.
        return ianaTimezoneToGraphQLEnum('UTC');
    }
}

export function graphqlTimezoneToIana(value: string): string {
    const timezone = value.trim();
    const ianaFromEnum = graphqlEnumToIana[timezone];
    if (ianaFromEnum) {
        return ianaFromEnum;
    }

    throw new Error(`Invalid timezone "${timezone}".`);
}

export function getTimezoneGraphqlEnumOptions(): string[] {
    return Object.values(ianaToGraphqlEnum);
}
